import { useEffect, useMemo, useState } from "preact/hooks";
import { AuthError, customerFromToken, loadTokens, logout, startLogin } from "../auth/auth.ts";
import { Logo } from "../components/Logo.tsx";
import { appName, config } from "../config.ts";
import { defineMessages, intlLocale, useLocale, useT } from "../i18n/index.ts";
import { Icon } from "../icons/Icon.tsx";
import { progressLabel } from "../progress.ts";
import { mobileApps, mobilePlatform, openApp } from "../shop/app-links.ts";
import { fetchConversations, lastChatByProduct, startedConversations, type LibraryConversation } from "../shop/conversations.ts";
import { fetchLibrary, matchesSearch, readability, type ShopBook } from "../shop/library.ts";
import { filterBooks, libraryFilters, librarySorts, sortBooks, type BookActivity, type LibraryFilter, type LibrarySort } from "../shop/librarySort.ts";
import { syncedProgress } from "../bookSync.ts";
import { loadLibraryOrder, loadLibraryView, loadProgress, saveLibraryOrder, saveLibraryView, type LibraryOrder, type LibraryView } from "../storage.ts";
import { syncFor } from "../sync.ts";

type State = { kind: "loading" } | { kind: "ready"; books: ShopBook[] } | { kind: "error" };

const accountName = config.brand === "chatnovel" ? "ChatNovel" : "My Smart Book";

const messages = defineMessages(
  {
    welcome: "Bienvenue sur notre lecteur web",
    welcomeText: "Connectez-vous à votre compte {account} pour accéder à vos livres.",
    signIn: "Se connecter",
    signOut: "Se déconnecter",
    loadError: "Votre bibliothèque n'a pas pu être chargée. Réessayez dans un instant.",
    libraryOf: "Bibliothèque de {name}",
    myLibrary: "Ma bibliothèque",
    searchPlaceholder: "Titre ou auteur",
    searchLabel: "Rechercher un titre ou un auteur",
    view: "Affichage",
    grid: "Grille",
    list: "Liste",
    loading: "Chargement de vos livres…",
    empty: "Votre bibliothèque est vide. Les livres achetés sur {app} apparaîtront ici.",
    noMatch: "Aucun livre ne correspond à « {query} ».",
    unsupported: "Format non pris en charge",
    excerpt: "Extrait",
    progress: "Progression : {label}",
    readInApp: "Lire dans l'app",
    protected: "Ce livre est protégé : il se lit dans l'application.",
    sections: "Bibliothèque",
    books: "Livres",
    chats: "Discussions",
    sortLabel: "Trier les livres",
    sort_recent: "Lus récemment",
    sort_chats: "Derniers chats",
    sort_title: "Titre A–Z",
    sort_author: "Auteur A–Z",
    filterLabel: "Filtrer les livres",
    filter_all: "Tous les livres",
    filter_reading: "En cours",
    filter_excerpts: "Extraits",
    filter_smartbooks: "Smartbooks",
    noneInFilter: "Aucun livre dans « {filter} ».",
    chatsLoading: "Chargement de vos discussions…",
    chatsError: "Vos discussions n'ont pas pu être chargées.",
    noChats: "Pas encore de discussion. Ouvrez un smartbook et posez-lui une question !",
    withPerson: "Avec {name}",
    withBook: "Avec le livre",
    lastMessage: "Dernier message : {date}",
  },
  {
    welcome: "Welcome to our web reader",
    welcomeText: "Sign in to your {account} account to access your books.",
    signIn: "Sign in",
    signOut: "Sign out",
    loadError: "Your library couldn't be loaded. Please try again in a moment.",
    libraryOf: "{name}'s library",
    myLibrary: "My library",
    searchPlaceholder: "Title or author",
    searchLabel: "Search for a title or an author",
    view: "View",
    grid: "Grid",
    list: "List",
    loading: "Loading your books…",
    empty: "Your library is empty. Books you buy on {app} will appear here.",
    noMatch: "No books match “{query}”.",
    unsupported: "Unsupported format",
    excerpt: "Excerpt",
    progress: "Progress: {label}",
    readInApp: "Read in the app",
    protected: "This book is protected: it can only be read in the app.",
    sections: "Library",
    books: "Books",
    chats: "Chats",
    sortLabel: "Sort books",
    sort_recent: "Recently read",
    sort_chats: "Recent chats",
    sort_title: "Title A–Z",
    sort_author: "Author A–Z",
    filterLabel: "Filter books",
    filter_all: "All books",
    filter_reading: "In progress",
    filter_excerpts: "Excerpts",
    filter_smartbooks: "Smartbooks",
    noneInFilter: "No books in “{filter}”.",
    chatsLoading: "Loading your chats…",
    chatsError: "Your chats couldn't be loaded.",
    noChats: "No chats yet. Open a smartbook and ask it a question!",
    withPerson: "With {name}",
    withBook: "With the book",
    lastMessage: "Last message: {date}",
  },
);

export function Library() {
  const tokens = loadTokens();
  if (!tokens) return <Welcome />;
  return <Shelf firstName={customerFromToken(tokens.accessToken)?.firstName} />;
}

function Welcome() {
  const t = useT(messages);
  return (
    <main class="page page--center">
      <div class="welcome">
        {config.brand === "mysmartbook" ? <Logo class="welcome__logo" /> : <p class="welcome__name">{appName}</p>}
        <h1>{t("welcome")}</h1>
        <p>{t("welcomeText", { account: accountName })}</p>
        <button type="button" class="reader-button reader-button--primary welcome__button" onClick={() => void startLogin()}>
          {t("signIn")}
        </button>
      </div>
    </main>
  );
}

function Shelf({ firstName }: { firstName?: string }) {
  const t = useT(messages);
  const [state, setState] = useState<State>({ kind: "loading" });
  const [query, setQuery] = useState("");
  const [view, setView] = useState<LibraryView>(loadLibraryView);
  const changeView = (next: LibraryView) => {
    setView(next);
    saveLibraryView(next);
  };
  const [order, setOrder] = useState<LibraryOrder>(loadLibraryOrder);
  const changeOrder = (patch: Partial<LibraryOrder>) => {
    const next = { ...order, ...patch };
    setOrder(next);
    saveLibraryOrder(next);
  };
  const [section, setSection] = useState<"books" | "chats">("books");
  // Les discussions (serveur IA) : pour l'onglet Discussions et le tri par derniers chats. undefined : en chargement.
  const [conversations, setConversations] = useState<LibraryConversation[] | null>();
  useEffect(() => {
    fetchConversations().then(setConversations, () => setConversations(null));
  }, []);

  // Les positions des autres appareils : la bibliothèque se redessine quand elles arrivent.
  const [syncedAt, setSyncedAt] = useState(0);
  useEffect(() => {
    void syncFor(loadTokens()?.accessToken)
      ?.sync()
      .then((ok) => ok && setSyncedAt(Date.now()));
  }, []);

  useEffect(() => {
    fetchLibrary()
      .then((books) => setState({ kind: "ready", books }))
      .catch((e) => {
        if (e instanceof AuthError) {
          logout();
          window.location.reload();
          return;
        }
        setState({ kind: "error" });
      });
  }, []);

  const books = state.kind === "ready" ? state.books : [];
  const locale = useLocale();
  const sync = syncFor(loadTokens()?.accessToken);
  const lastChat = useMemo(() => lastChatByProduct(conversations ?? []), [conversations]);
  const activity: BookActivity = {
    readAt: (book) => {
      const at = sync?.get("location", book.productId)?.updated_at;
      return at ? Date.parse(at) : undefined;
    },
    chatAt: (book) => lastChat.get(book.productId),
    progress: (book) => syncedProgress(sync, book.productId) ?? loadProgress(book.id),
  };
  const shown = useMemo(
    () => sortBooks(filterBooks(books, order.filter, activity), order.sort, activity, intlLocale(locale)).filter((book) => matchesSearch(book, query)),
    [books, query, order, lastChat, locale, syncedAt],
  );

  return (
    <main class="page">
      <header class="page-header">
        <div>
          <p class="page-eyebrow">{appName}</p>
          <h1>{firstName ? t("libraryOf", { name: firstName }) : t("myLibrary")}</h1>
        </div>
        <button
          type="button"
          class="msb-round-button"
          aria-label={t("signOut")}
          title={t("signOut")}
          onClick={() => {
            logout();
            window.location.assign("/");
          }}
        >
          <Icon name="logout" />
        </button>
      </header>

      {books.length > 0 && (
        <div class="shelf-tabs view-toggle" role="tablist" aria-label={t("sections")}>
          {(["books", "chats"] as const).map((key) => (
            <button key={key} type="button" role="tab" aria-selected={section === key} aria-pressed={section === key} onClick={() => setSection(key)}>
              {t(key)}
            </button>
          ))}
        </div>
      )}

      {books.length > 0 && section === "books" && (
        <div class="shelf-search">
          <label class="search-field">
            <Icon name="search" size={20} />
            <input
              type="search"
              value={query}
              onInput={(e) => setQuery(e.currentTarget.value)}
              placeholder={t("searchPlaceholder")}
              aria-label={t("searchLabel")}
              autocomplete="off"
              enterKeyHint="search"
            />
          </label>
          <select class="shelf-select" aria-label={t("sortLabel")} value={order.sort} onChange={(e) => changeOrder({ sort: e.currentTarget.value as LibrarySort })}>
            {librarySorts.map((key) => (
              <option key={key} value={key}>
                {t(`sort_${key}`)}
              </option>
            ))}
          </select>
          <select class="shelf-select" aria-label={t("filterLabel")} value={order.filter} onChange={(e) => changeOrder({ filter: e.currentTarget.value as LibraryFilter })}>
            {libraryFilters.map((key) => (
              <option key={key} value={key}>
                {t(`filter_${key}`)}
              </option>
            ))}
          </select>
          <div class="view-toggle" role="group" aria-label={t("view")}>
            <button type="button" aria-pressed={view === "grid"} onClick={() => changeView("grid")} aria-label={t("grid")} title={t("grid")}>
              <Icon name="gridView" size={22} />
            </button>
            <button type="button" aria-pressed={view === "list"} onClick={() => changeView("list")} aria-label={t("list")} title={t("list")}>
              <Icon name="listView" size={22} />
            </button>
          </div>
        </div>
      )}

      {state.kind === "loading" && <p class="page-status">{t("loading")}</p>}
      {state.kind === "error" && (
        <p class="page-status" role="alert">
          {t("loadError")}
        </p>
      )}
      {state.kind === "ready" && books.length === 0 && (
        <p class="page-status">{t("empty", { app: appName })}</p>
      )}
      {section === "chats" && books.length > 0 && <Conversations conversations={conversations} books={books} />}
      {section === "books" && books.length > 0 && shown.length === 0 && (
        <p class="page-status">
          {query.trim() ? t("noMatch", { query: query.trim() }) : t("noneInFilter", { filter: t(`filter_${order.filter}`) })}
        </p>
      )}
      {section === "books" && shown.length > 0 && (
        <ul class={view === "list" ? "shelf-list" : "shelf"}>
          {shown.map((book) => (view === "list" ? <BookRow key={book.id} book={book} /> : <BookCard key={book.id} book={book} />))}
        </ul>
      )}
    </main>
  );
}

/** Les discussions commencées, de la plus récente à la plus ancienne : chacune rouvre son livre, chat affiché. */
function Conversations({ conversations, books }: { conversations: LibraryConversation[] | null | undefined; books: ShopBook[] }) {
  const t = useT(messages);
  const locale = useLocale();
  if (conversations === undefined) return <p class="page-status">{t("chatsLoading")}</p>;
  if (conversations === null) {
    return (
      <p class="page-status" role="alert">
        {t("chatsError")}
      </p>
    );
  }
  const byProduct = new Map(books.map((book) => [book.productId, book]));
  const started = startedConversations(conversations).filter((conversation) => conversation.product_ids.some((id) => byProduct.has(id)));
  if (started.length === 0) return <p class="page-status">{t("noChats")}</p>;
  const date = new Intl.DateTimeFormat(intlLocale(locale), { dateStyle: "medium", timeStyle: "short" });
  return (
    <ul class="shelf-list">
      {started.map((conversation) => {
        const book = byProduct.get(conversation.product_ids.find((id) => byProduct.has(id))!)!;
        const person = conversation.kind === "character" ? conversation.character?.name || conversation.name : undefined;
        const details = (
          <div class="book-row__details">
            <span class="book-row__title">{book.title}</span>
            <span class="book-row__kind">
              <Icon name="chat" size={20} />
              {person ? t("withPerson", { name: person }) : t("withBook")}
            </span>
            <span class="book-row__author">{t("lastMessage", { date: date.format(new Date(conversation.last_message_at!)) })}</span>
          </div>
        );
        return (
          <li key={conversation.id}>
            {readability(book) === "web" ? (
              <a class="book-row" href={`${readHref(book)}?discussion`}>
                <Cover book={book} />
                {details}
              </a>
            ) : (
              <div class="book-row">
                <Cover book={book} />
                {details}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function Cover({ book }: { book: ShopBook }) {
  return (
    <div class="book-cover">
      {book.coverImage ? <img src={book.coverImage} alt="" loading="lazy" /> : <span class="book-cover__placeholder">{book.title}</span>}
    </div>
  );
}

const authorsOf = (book: ShopBook) => book.creators.map((c) => c.displayName).join(", ");
const readHref = (book: ShopBook) => `/lire/${encodeURIComponent(book.id)}`;

/** Grille : la couverture et sa barre de statut, comme dans l'app ; titre et auteur au survol. */
function BookCard({ book }: { book: ShopBook }) {
  const t = useT(messages);
  const status = readability(book);
  const author = authorsOf(book);
  const name = author ? `${book.title}, ${author}` : book.title;
  if (status === "web") {
    return (
      <li class="book">
        <a class="book__link" href={readHref(book)} aria-label={name} title={name}>
          <Cover book={book} />
          <StatusBar book={book} />
        </a>
      </li>
    );
  }
  return (
    <li class="book" title={name}>
      <Cover book={book} />
      {status === "app-only" ? <OpenInApp /> : <span class="book-tag">{t("unsupported")}</span>}
    </li>
  );
}

/** Liste : couverture, titre, auteur, type de livre et barre de statut, comme dans l'app. */
function BookRow({ book }: { book: ShopBook }) {
  const t = useT(messages);
  const status = readability(book);
  const author = authorsOf(book);
  const details = (
    <div class="book-row__details">
      <span class="book-row__title">{book.title}</span>
      {author && <span class="book-row__author">{author}</span>}
      <span class="book-row__kind">
        <Icon name={book.isSmartbook ? "smartbook" : "ebook"} size={20} />
        {book.isSmartbook ? "Smartbook" : "Ebook"}
      </span>
      {status === "web" && <StatusBar book={book} compact />}
      {status === "app-only" && <OpenInApp />}
      {status === "unsupported" && <span class="book-tag">{t("unsupported")}</span>}
    </div>
  );
  return (
    <li>
      {status === "web" ? (
        <a class="book-row" href={readHref(book)}>
          <Cover book={book} />
          {details}
        </a>
      ) : (
        <div class="book-row">
          <Cover book={book} />
          {details}
        </div>
      )}
    </li>
  );
}

/** Icône livre / smartbook et barre de progression, comme sous les couvertures de l'app. */
function StatusBar({ book, compact = false }: { book: ShopBook; compact?: boolean }) {
  const t = useT(messages);
  const locale = useLocale();
  const progress = book.isExtract ? undefined : (syncedProgress(syncFor(loadTokens()?.accessToken), book.productId) ?? loadProgress(book.id));
  const label = progressLabel(progress, book.isExtract, locale);
  return (
    <div class={compact ? "book-status book-status--compact" : "book-status"}>
      {/* En liste, le type de livre est déjà affiché au-dessus : la barre seule, comme dans l'app. */}
      {!compact && <Icon name={book.isSmartbook ? "smartbook" : "ebook"} size={20} class="book-status__icon" />}
      <span class="book-status__bar" role="img" aria-label={book.isExtract ? t("excerpt") : t("progress", { label: label.toLowerCase() })}>
        <span class="book-status__fill" style={{ width: `${(progress ?? 0) * 100}%` }} />
        <span class="book-status__text">{label}</span>
      </span>
    </div>
  );
}

/** Livre protégé (LCP) : il se lit dans l'app, que le bouton ouvre sur mobile. */
function OpenInApp() {
  const t = useT(messages);
  const app = mobileApps[config.brand];
  const platform = mobilePlatform(navigator.userAgent, navigator.maxTouchPoints);
  // Sur ordinateur, pas d'app à ouvrir : le bouton propose les deux stores.
  const [stores, setStores] = useState(false);
  if (!app) return <span class="book-tag">{t("readInApp")}</span>;
  return (
    <div class="book-app">
      <button
        type="button"
        class="book-app__button"
        title={t("protected")}
        onClick={() => (platform === "other" ? setStores((open) => !open) : openApp(app, platform))}
        aria-expanded={platform === "other" ? stores : undefined}
      >
        <Icon name="phone" size={16} />
        {t("readInApp")}
      </button>
      {stores && (
        <span class="book-app__stores">
          <a href={app.playStoreUrl} target="_blank" rel="noopener">
            Google Play
          </a>
          <a href={app.appStoreUrl} target="_blank" rel="noopener">
            App Store
          </a>
        </span>
      )}
    </div>
  );
}
